import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { GoogleLogin } from '@react-oauth/google';
import { motion } from 'framer-motion';
import { Bot, Sparkles, User, Sun, Moon, Image, Video, Zap, Shield, Star } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

// Axios base configuration
axios.defaults.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5003';
axios.defaults.headers.common['Content-Type'] = 'application/json';

const Login = () => {
  const { login, loginWithUser } = useAuth();
  const { toggleTheme, isDark } = useTheme();
  const [isLoading, setIsLoading] = useState(false);

  const handleGuestLogin = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post('/api/auth/guest');
      
      const { access_token, user } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      loginWithUser(user);
    } catch (error) {
      // Guest login error
      toast.error('Misafir girişi sırasında hata oluştu: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setIsLoading(true);
    try {
      await login(credentialResponse.credential);
    } catch (error) {
      toast.error('Google ile giriş yapılamadı: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = (error) => {
    toast.error('Google ile giriş yapılamadı. Lütfen misafir olarak devam edin.');
  };

  return (
    <div className="h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center p-4 overflow-hidden relative">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          animate={{
            x: [0, 100, 0],
            y: [0, -100, 0],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute top-20 left-20 w-32 h-32 bg-blue-500/10 rounded-full blur-xl"
        />
        <motion.div
          animate={{
            x: [0, -100, 0],
            y: [0, 100, 0],
            rotate: [360, 180, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute bottom-20 right-20 w-40 h-40 bg-purple-500/10 rounded-full blur-xl"
        />
        <motion.div
          animate={{
            x: [0, 50, 0],
            y: [0, -50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-1/2 left-1/4 w-24 h-24 bg-pink-500/10 rounded-full blur-lg"
        />
      </div>

      {/* Theme Toggle Button */}
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={toggleTheme}
        className="absolute top-4 right-4 p-3 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-all duration-200 z-10 backdrop-blur-sm"
        title={isDark ? 'Aydınlık temaya geç' : 'Karanlık temaya geç'}
      >
        {isDark ? <Sun className="w-6 h-6" /> : <Moon className="w-6 h-6" />}
      </motion.button>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-md w-full max-h-full overflow-y-auto relative z-10"
      >
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="flex flex-col items-center mb-6"
          >
            <motion.div
              animate={{
                rotate: [0, 10, -10, 0],
                scale: [1, 1.05, 1],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="relative"
            >
              <Bot className="w-24 h-24 text-blue-400 mb-4 drop-shadow-lg" />
              <motion.div
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.5, 1, 0.5],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center"
              >
                <Star className="w-4 h-4 text-yellow-800" />
              </motion.div>
            </motion.div>
            <h1 className="text-6xl font-extrabold text-white mb-2 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              AI Chat
            </h1>
            <p className="text-gray-300 text-xl font-medium">Yapay Zeka Asistanınızla Tanışın</p>
          </motion.div>
        </div>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mb-8 space-y-3"
        >
          <motion.div
            whileHover={{ scale: 1.02, x: 5 }}
            className="flex items-center justify-center space-x-4 text-gray-300 bg-white/5 backdrop-blur-sm rounded-lg p-3 border border-white/10"
          >
            <Sparkles className="w-6 h-6 text-purple-400" />
            <span className="text-gray-200 text-lg font-medium">Akıllı Sohbet</span>
          </motion.div>
          <motion.div
            whileHover={{ scale: 1.02, x: 5 }}
            className="flex items-center justify-center space-x-4 text-gray-300 bg-white/5 backdrop-blur-sm rounded-lg p-3 border border-white/10"
          >
            <Image className="w-6 h-6 text-green-400" />
            <span className="text-gray-200 text-lg font-medium">Görsel Üretimi</span>
          </motion.div>
          <motion.div
            whileHover={{ scale: 1.02, x: 5 }}
            className="flex items-center justify-center space-x-4 text-gray-300 bg-white/5 backdrop-blur-sm rounded-lg p-3 border border-white/10"
          >
            <Video className="w-6 h-6 text-red-400" />
            <span className="text-gray-200 text-lg font-medium">Video Üretimi</span>
          </motion.div>
          <motion.div
            whileHover={{ scale: 1.02, x: 5 }}
            className="flex items-center justify-center space-x-4 text-gray-300 bg-white/5 backdrop-blur-sm rounded-lg p-3 border border-white/10"
          >
            <Shield className="w-6 h-6 text-blue-400" />
            <span className="text-gray-200 text-lg font-medium">Güvenli & Hızlı</span>
          </motion.div>
        </motion.div>

        {/* Login Buttons */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="space-y-4"
        >
          {/* Guest Login */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleGuestLogin}
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 sm:px-8 py-4 rounded-xl text-lg font-semibold hover:from-blue-600 hover:to-purple-700 transition-all duration-200 flex items-center justify-center space-x-3 disabled:opacity-50 btn-mobile shadow-lg hover:shadow-xl"
          >
            <User className="w-6 h-6" />
            <span>Misafir Olarak Devam Et</span>
            <Zap className="w-5 h-5" />
          </motion.button>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-600"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-gray-900 text-gray-400 font-medium">veya</span>
            </div>
          </div>

          {/* Google Login */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              theme="dark"
              size="large"
              width="100%"
              text="signin_with"
              shape="pill"
              locale="tr"
            />
          </motion.div>
          
          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-4 flex items-center justify-center space-x-3"
            >
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
              <span className="text-gray-300 font-medium">Giriş yapılıyor...</span>
            </motion.div>
          )}
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-8 text-center"
        >
          <p className="text-gray-400 text-sm">
            &copy; 2025 AI Chat. Tüm hakları saklıdır.
          </p>
          <p className="text-gray-500 text-xs mt-1">
            Güvenli, hızlı ve akıllı yapay zeka deneyimi
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Login;